<template>
  <div class="p-6 dark:text-gray-50" v-if="canAdd || user?.is_staff">
    <div class="flex space-x-3 mb-6">
      <button
        @click="goBack"
        class="bg-gray-600 text-white px-4 py-1.5 rounded-md hover:bg-gray-700 text-sm flex items-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
        </svg>
        Back
      </button>
      <h1 class="text-2xl font-semibold dark:text-white">{{ isEditMode ? 'Edit' : 'Add' }} Central Server</h1>
    </div>

    <!-- Page-level Error Banner -->
    <div v-if="pageError" class="bg-red-50 dark:bg-red-900/20 border border-red-400 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4 flex items-center">
      <svg class="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
      </svg>
      <span class="font-medium">{{ pageError }}</span>
    </div>

    <form @submit.prevent="submitForm" class="space-y-4 bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <!-- Name -->
      <div class="flex flex-col">
        <label for="name" class="mb-1 font-medium">
          Name<span class="text-red-500">*</span>
        </label>
        <input
          id="name"
          v-model="formData.name"
          type="text"
          placeholder="e.g., Central-Server-1"
          :class="inputClass('name')"
        />
        <FieldError :message="fieldErrors.name" />
      </div>

      <!-- Fortigate IP -->
      <div class="flex flex-col">
        <label for="fortigate_ip" class="mb-1 font-medium">
          Fortigate IP<span class="text-red-500">*</span>
        </label>
        <input
          id="fortigate_ip"
          v-model="formData.fortigate_ip"
          type="text"
          placeholder="e.g., 192.168.1.1"
          :class="inputClass('fortigate_ip')"
        />
        <FieldError :message="fieldErrors.fortigate_ip" />
      </div>

      <!-- API Port -->
      <div class="flex flex-col">
        <label for="api_port" class="mb-1 font-medium">
          API Port
        </label>
        <input
          id="api_port"
          v-model.number="formData.api_port"
          type="number"
          min="1"
          max="65535"
          class="border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <FieldError :message="fieldErrors.api_port" />
      </div>

      <!-- API Key -->
      <div class="flex flex-col">
        <label for="api_key" class="mb-1 font-medium">
          API Key<span class="text-red-500">*</span>
        </label>
        <input
          id="api_key"
          v-model="formData.api_key"
          type="text"
          placeholder="Enter API key"
          :class="inputClass('api_key')"
        />
        <FieldError :message="fieldErrors.api_key" />
      </div>

      <!-- Actions -->
      <div class="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          @click="goBack"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {{ isEditMode ? 'Update' : 'Create' }} Central Server
        </button>
      </div>
    </form>
  </div>

  <PageError v-else-if="permsLoaded" :error="'Permission denied'" :errorCode="'403'" />
</template>

<script>
import { createSSHCollectorMedicross, getSSHCollectorMedicrossById, updateSSHCollectorMedicross } from "@/services/sshCollectorService";
import FieldError from "@/components/FieldError.vue";
import PageError from "@/components/PageError.vue";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "AddSSHCollectorMedicrossSite",
  components: { FieldError, PageError },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      fieldErrors: {},
      pageError: null,
      formData: {
        name: "",
        fortigate_ip: "",
        api_port: 443,
        api_key: "",
      },
      canAdd: false,
    };
  },
  computed: {
    currentSchema() { return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null; },
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    configId() {
      return this.$route.params.configId;
    },
    jobId() {
      return this.$route.params.jobId;
    },
    isEditMode() {
      return !!this.jobId;
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(() => Promise.resolve());
    await this.checkPermissions();
    if (this.isEditMode) {
      await this.loadExistingJob();
    }
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canAdd = await useStore.hasPermission("Can add SSH Collector Config");
      }
    },
    async loadExistingJob() {
      try {
        const job = await getSSHCollectorMedicrossById(this.jobId, this.currentSchema);
        this.formData.name = job.name || "";
        this.formData.fortigate_ip = job.fortigate_ip || "";
        this.formData.api_port = job.api_port || 443;
        this.formData.api_key = job.api_key || "";
      } catch (error) {
        console.error("Error loading central server:", error);
        alert("Failed to load central server data");
        this.goBack();
      }
    },
    inputClass(field) {
      return [
        'border rounded px-3 py-2 focus:outline-none focus:ring-2 dark:bg-gray-600',
        this.fieldErrors[field] ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-300 focus:ring-blue-500'
      ];
    },
    goBack() {
      this.$router.push({ name: "SSHCollectorDetails", params: { id: this.configId }, query: { schemaName: this.currentSchema } });
    },
    async submitForm() {
      this.pageError = null;
      this.fieldErrors = {};

      if (!this.formData.name) {
        this.fieldErrors.name = "Name is required";
        return;
      }
      if (!this.formData.fortigate_ip) {
        this.fieldErrors.fortigate_ip = "Fortigate IP is required";
        return;
      }
      if (!this.formData.api_key) {
        this.fieldErrors.api_key = "API Key is required";
        return;
      }

      const data = {
        ssh_collector_config: parseInt(this.configId),
        name: this.formData.name,
        fortigate_ip: this.formData.fortigate_ip,
        api_port: this.formData.api_port,
        api_key: this.formData.api_key,
      };

      try {
        if (this.isEditMode) {
          await updateSSHCollectorMedicross(this.jobId, data, this.currentSchema);
        } else {
          await createSSHCollectorMedicross(data, this.currentSchema);
        }
        this.goBack();
      } catch (error) {
        this.pageError = null;
        this.fieldErrors = {};

        const errData = error?.response?.data;
        if (errData) {
          Object.keys(errData).forEach((key) => {
            const errorMsg = Array.isArray(errData[key]) ? errData[key][0] : errData[key];
            this.fieldErrors[key] = errorMsg;
          });

          if (errData.ssh_collector_config || errData.non_field_errors) {
            let errorMessage = errData.ssh_collector_config?.[0] || errData.ssh_collector_config ||
                               errData.non_field_errors?.[0] || errData.non_field_errors;

            if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('mimir')) {
              errorMessage = "Your organization tenant is not configured. Please contact Splitpoint support for assistance.";
            }

            this.pageError = errorMessage || "Please correct the errors below";
          } else if (Object.keys(errData).length > 0) {
            this.pageError = "Please correct the errors below";
          }
        } else {
          this.pageError = `Failed to ${this.isEditMode ? 'update' : 'create'} central server. Please try again.`;
        }

        console.error(`Error ${this.isEditMode ? 'updating' : 'creating'} central server:`, error);
      }
    },
  },
};
</script>
